import type { PackageJsonExports } from '@ms-cloudpack/common-types';
import { mergeObjects } from '@ms-cloudpack/package-utilities';
import { satisfies } from 'semver';
import { mergePackageSettings } from '../packageSettings/mergePackageSettings.js';
import { builtInSettings, type BuiltInSettings } from './builtInSettings.js';

/**
 * Get any applicable built-in settings for the given package name and version.
 *
 * NOTE: This should usually not be called directly from outside the `@ms-cloudpack/config` package.
 * It's just exported so tests can check their assumptions about what built-in settings exist.
 */
export function getBuiltInSettings(params: { name: string; version: string | undefined }): BuiltInSettings | undefined {
  const { name, version } = params;
  if (!version) {
    return undefined;
  }

  // As of writing, there should be at most one matching setting, but find all of them
  // and handle merging just to be safe.
  const matchingSettings = Object.entries(builtInSettings[name] || {})
    .filter(([range]) => satisfies(version, range))
    .map(([, setting]) => setting);

  if (!matchingSettings.length) {
    return undefined;
  }

  const mergedSettings: BuiltInSettings = {};

  const packageJsons = matchingSettings
    .map((s) => s.packageJson)
    .filter((s): s is NonNullable<BuiltInSettings['packageJson']> => !!s);
  if (packageJsons.length) {
    mergedSettings.packageJson = mergeObjects(packageJsons);
    if (mergedSettings.packageJson.exports) {
      // Currently, some of the cloudpack code can potentially mutate package.json `exports`.
      // Copy the object to avoid accidentally mutating the constant.
      mergedSettings.packageJson.exports = JSON.parse(
        JSON.stringify(mergedSettings.packageJson.exports),
      ) as PackageJsonExports;
    }
  }

  const packageSettings = matchingSettings
    .map((s) => s.packageSettings)
    .filter((s): s is NonNullable<BuiltInSettings['packageSettings']> => !!s);
  if (packageSettings.length) {
    mergedSettings.packageSettings = mergePackageSettings(packageSettings);
  }

  return mergedSettings;
}
